import { Alert } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import Tabs from '../../../../components/Tabs';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import { disableNavigationGuard } from '../../../../hooks/useNavigationGuard';
import useQueryErrorNotifier from '../../../../hooks/useQueryErrorNotifier';
import {
  CrewMemberRole,
  EntityStatus,
  ErrorType,
  SelectItem,
  ServiceType,
} from '../../../../types/core';
import { UpdatedJourneyLog } from '../../../../types/journey';
import { DAYJS_VISUALIZATION_DATE_FORMAT } from '../../../../utils/date';
import { validateNumber } from '../../../../utils/validation';
import { JourneyTemplateSubPageContext } from '../types';
import PassengersForm from './PassengersForm';
import { CrewMemberSelectItem, JourneyLogFormValues } from './types';
import VesselForm from './VesselForm';
import { CommonMainPanelForm } from '../../../../components/AdminTemplates';
import useIotsAdminForm from '../../../../hooks/useIotsAdminForm';
import useFreshDataEnsurer from '../../../../hooks/useFreshDataEnsurer';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';

interface LogVesselSelectItem extends SelectItem {
  isPrimary?: boolean;
}

const JourneyLogPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    journeyId,
    journey,
    logManagement,
    refetchJourney,
    navigateToJourneyDetails,
  } = useOutletContext<JourneyTemplateSubPageContext>();
  const { isAllowed, isPending, isFetching, journeyLog, refetch } =
    logManagement;
  const {
    getCrewMembers,
    getVessels,
    updateJourneyLog,
    getContractAssignedVessels,
  } = useIotsApi();

  const {
    data: fetchedCrewMembers = [],
    isFetching: isFetchingCrewMembers,
    error: crewMembersError,
  } = useQuery({
    queryKey: ['crew-members'],
    queryFn: () => getCrewMembers(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: crewMembers =>
      // Simple default sorting by full name
      [...crewMembers].sort(
        (a, b) =>
          a.firstName.localeCompare(b.firstName) ||
          a.lastName.localeCompare(b.lastName),
      ),
  });

  const {
    data: fetchedVessels = [],
    isFetching: isFetchingVessels,
    error: vesselsError,
  } = useQuery({
    queryKey: ['vessels'],
    queryFn: () => getVessels(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: vessels =>
      // Simple default sorting by name
      [...vessels].sort((a, b) => a.vesselName.localeCompare(b.vesselName)),
  });

  const {
    data: assignedVessels = null,
    isFetching: isFetchingAssignedVessels,
    error: assignedVesselsError,
  } = useQuery({
    queryKey: ['contract', journey.contractId ?? '', 'vessels'],
    queryFn: () => getContractAssignedVessels(journey.contractId ?? ''),
    enabled: Boolean(journey.contractId),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useQueryErrorNotifier([crewMembersError, vesselsError, assignedVesselsError]);

  useFreshDataEnsurer(isFetching, refetch);

  const carriesCargo = [
    ServiceType.FerryPassengerAndLightCargo,
    ServiceType.FerryHeavyCargo,
  ].includes(journey.serviceType);

  const carriesVehicles =
    journey.serviceType === ServiceType.FerryRollOnRollOff;

  const carriesPassengers = journey.serviceType !== ServiceType.FerryHeavyCargo;

  const { crewMembers, crewMembersMap, journeyDay } = useMemo(() => {
    const crewMembers: CrewMemberSelectItem[] = [];
    const crewMembersMap: Record<string, CrewMemberSelectItem> = {};
    const journeyDay = dayjs(journey.day);

    fetchedCrewMembers.forEach(crewMember => {
      // Only crew members from the journey operator can be added
      if (crewMember.operatorId !== journey.operatorId) {
        return;
      }

      const selectItem: CrewMemberSelectItem = {
        value: crewMember.id,
        label: `${crewMember.firstName} ${crewMember.lastName}`,
        isSkipper: crewMember.role === CrewMemberRole.Skipper,
      };

      crewMembersMap[selectItem.value] = selectItem;

      // only crew members not deleted on the day of the trip are displayed
      const deleteDay = dayjs(crewMember.deleteDate);
      if (!deleteDay.isValid() || deleteDay.isAfter(journeyDay))
        crewMembers.push(selectItem);
    });

    return { crewMembers, crewMembersMap, journeyDay };
  }, [fetchedCrewMembers, journey]);

  const { assignableVessels, vesselsMap } = useMemo(() => {
    const assignableVessels: LogVesselSelectItem[] = [];
    const vesselsMap: Record<string, LogVesselSelectItem> = {};

    if (journey.contractId && !assignedVessels) {
      return { assignableVessels, vesselsMap };
    }

    const assignedVesselsMap: Record<string, boolean> = {};

    if (assignedVessels) {
      assignedVesselsMap[assignedVessels.primary.vesselId] = true;
      assignedVessels.backups.forEach(vessel => {
        assignedVesselsMap[vessel.vesselId] = false;
      });
    }

    fetchedVessels.forEach(vessel => {
      // Only vessels from the journey operator are considered
      if (vessel.operatorId !== journey.operatorId) {
        return;
      }

      const selectItem: LogVesselSelectItem = {
        value: vessel.id,
        label: vessel.vesselName,
        isPrimary: Boolean(assignedVesselsMap[vessel.id]),
      };

      vesselsMap[selectItem.value] = selectItem;

      // If applies, only assigned vessels will be able to be selected
      if (
        !assignedVessels ||
        typeof assignedVesselsMap[vessel.id] === 'boolean'
      ) {
        assignableVessels.push(selectItem);
      }
    });

    return { assignableVessels, vesselsMap };
  }, [fetchedVessels, journey, assignedVessels]);

  const {
    generalValidationMessages,
    vesselValidationMessages,
    passengersValidationMessages,
  } = useMemo(() => {
    const generalValidationMessages = [] as string[];
    const vesselValidationMessages = [] as string[];
    const passengersValidationMessages = [] as string[];

    journeyLog?.validation.forEach(validation => {
      switch (validation.type) {
        case ErrorType.EmptyVesselCertificate:
          vesselValidationMessages.push(
            t(`journey-log-validation.vessel-certificate-non-existant`),
          );
          break;
        case ErrorType.InvalidJourneyVesselCertificate:
          vesselValidationMessages.push(
            t(`journey-log-validation.vessel-certificate-not-found-for-date`, {
              DATE: journeyDay.format(DAYJS_VISUALIZATION_DATE_FORMAT),
            }),
          );
          break;
        case ErrorType.EmptySkipperCertificate:
          passengersValidationMessages.push(
            t(`journey-log-validation.skipper-certificate-non-existant`),
          );
          break;
        case ErrorType.InvalidJourneySkipperCertificate:
          passengersValidationMessages.push(
            t(`journey-log-validation.skipper-certificate-not-found-for-date`, {
              DATE: journeyDay.format(DAYJS_VISUALIZATION_DATE_FORMAT),
            }),
          );
          break;
        case ErrorType.InvalidCertificateCapacity:
          passengersValidationMessages.push(
            t(
              `journey-log-validation.vessel-certificate-for-passenger-details-not-found`,
            ),
          );
          break;
        case ErrorType.InvalidVesselInsurance:
          passengersValidationMessages.push(
            t(`journey-log-validation.vessel-insurance-not-found-for-date`, {
              DATE: journeyDay.format(DAYJS_VISUALIZATION_DATE_FORMAT),
            }),
          );
          break;
        case ErrorType.InvalidJourneyContract:
          generalValidationMessages.push(
            t(`journey-log-validation.journey-outside-contract-date-range`),
          );
          break;
        case ErrorType.InvalidJourneyRoute:
          generalValidationMessages.push(
            t(`journey-log-validation.journey-outside-route-date-range`),
          );
          break;
        default:
          generalValidationMessages.push(
            t(`journey-log-validation.unknown-error`, {
              ERROR: validation.type,
            }),
          );
          break;
      }
    });

    return {
      generalValidationMessages,
      vesselValidationMessages,
      passengersValidationMessages,
    };
  }, [journeyDay, journeyLog, t]);

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const updateJourneyLogMutation = useMutation({
    mutationFn: (updatedJourneyLog: UpdatedJourneyLog) =>
      updateJourneyLog(updatedJourneyLog),
  });

  const needsJourneyLog = ![
    EntityStatus.Submitted,
    EntityStatus.Invoiced,
  ].includes(journey.entityStatus);

  const shouldInputsBeDisabled =
    isFetching || updateJourneyLogMutation.isPending || !needsJourneyLog;

  const initialValues = useMemo<JourneyLogFormValues>(() => {
    if (!journeyLog) {
      return {
        vesselId:
          assignableVessels.find(({ isPrimary }) => isPrimary)?.value ?? null,
        fuel: '',
        cargoAmount: '',
        numberOfVehicles: '',
        skipperId: null,
        crewMemberIds: [],
        adultIslanders: (0).toString(),
        adultNonIslanders: (0).toString(),
        under18Islanders: (0).toString(),
        under18NonIslanders: (0).toString(),
        under05Islanders: (0).toString(),
        under05NonIslanders: (0).toString(),
        studentIslanders: (0).toString(),
        studentNonIslanders: (0).toString(),
        over65Islanders: (0).toString(),
        over65NonIslanders: (0).toString(),
      };
    }
    const { journeyLog: vesselDetails, passengerDetails } = journeyLog;
    return {
      vesselId: vesselDetails.vesselId ?? null,
      fuel: vesselDetails.fuel.toString(),
      cargoAmount: (vesselDetails.cargoAmount ?? 0).toString(),
      numberOfVehicles: (vesselDetails.noOfVehicles ?? 0).toString(),
      skipperId: passengerDetails.skipperId,
      crewMemberIds: passengerDetails.crew ?? [],
      adultIslanders: passengerDetails.adultIslander.toString(),
      adultNonIslanders: passengerDetails.adultNonIslander.toString(),
      under18Islanders: passengerDetails.underEighteenIslander.toString(),
      under18NonIslanders: passengerDetails.underEighteenNonIslander.toString(),
      under05Islanders: passengerDetails.underFiveIslander.toString(),
      under05NonIslanders: passengerDetails.underFiveNonIslander.toString(),
      studentIslanders: passengerDetails.studentsIslander.toString(),
      studentNonIslanders: passengerDetails.studentsNonIslander.toString(),
      over65Islanders: passengerDetails.overSixtyIslander.toString(),
      over65NonIslanders: passengerDetails.overSixtyNonIslander.toString(),
    };
  }, [journeyLog, assignableVessels]);

  const formManager = useIotsAdminForm<JourneyLogFormValues>(
    {
      // The only case where it is ensured the editing mode has to be displayed is for journeys with no real entity still created
      isEditingByDefault: !journeyId,
      initialValues,
      onEditionCancel: callback => {
        if (!journeyLog) {
          navigateToJourneyDetails();
        } else {
          callback();
        }
      },
      onSubmit: (values, callback) => {
        updateJourneyLogMutation.mutate(
          {
            journeyId,
            journey,
            log: {
              vesselId: values.vesselId as string,
              fuel: Number(values.fuel),
              cargoAmount: Number(values.cargoAmount),
              noOfVehicles: Number(values.numberOfVehicles),
            },
            passengerDetails: {
              skipperId: values.skipperId as string,
              crew: values.crewMemberIds.length ? values.crewMemberIds : null,
              adultIslander: Number(values.adultIslanders),
              adultNonIslander: Number(values.adultNonIslanders),
              underEighteenIslander: Number(values.under18Islanders),
              underEighteenNonIslander: Number(values.under18NonIslanders),
              underFiveIslander: Number(values.under05Islanders),
              underFiveNonIslander: Number(values.under05NonIslanders),
              studentsIslander: Number(values.studentIslanders),
              studentsNonIslander: Number(values.studentNonIslanders),
              overSixtyIslander: Number(values.over65Islanders),
              overSixtyNonIslander: Number(values.over65NonIslanders),
            },
          },
          {
            onSuccess: newJourneyLog => {
              const { journeyId: newJourneyId } = newJourneyLog;
              enqueueSnackbar({
                variant: 'success',
                message: t('content.journey-log-update-success'),
              });
              if (!journeyId) {
                void disableNavigationGuard(() =>
                  navigate(`/admin/journeys/${newJourneyId}/log`),
                );
              }
              callback();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.journey-log-update-error'),
                ),
              });
              updateJourneyLogMutation.reset();
            },
            onSettled: () => {
              /* There seems to be some kind of problem sometimes updating the log.
              Until we figure out what is happening, we always refetch all data for the existing
              journey and log in those cases (won't do anything on placeholders) */
              if (journeyId) {
                refetchJourney();
                refetch();
              }
            },
          },
        );
      },
      validate: (values, setError) => {
        const {
          vesselId,
          fuel,
          cargoAmount,
          numberOfVehicles,
          skipperId,
          crewMemberIds,
          ...numericPassengerValues
        } = values;

        if (!vesselId) {
          setError('vesselId', t('validation.required-field'));
        } else if (!vesselsMap[vesselId]) {
          setError('vesselId', t('validation.invalid-option'));
        }

        const validateExistingNonNegativeNumber = (
          key: keyof JourneyLogFormValues,
          value: string,
          allowDecimals = false,
        ) => {
          const result = validateNumber(value);
          if (!result.isNumber) {
            if (result.isEmpty) {
              setError(key, t('validation.required-field'));
            } else {
              setError(key, t('validation.must-be-numeric'));
            }
          } else {
            if (!allowDecimals && !result.isInteger) {
              setError(key, t(`validation.must-be-numeric-integer`));
            } else if (result.isNegative) {
              setError(
                key,
                t(
                  `validation.must-be-number-greater-than-or-equal-to-reference`,
                  { REFERENCE: '0' },
                ),
              );
            }
          }
        };

        validateExistingNonNegativeNumber('fuel', fuel, true);

        if (carriesCargo) {
          validateExistingNonNegativeNumber('cargoAmount', cargoAmount, true);
        }

        if (carriesVehicles) {
          validateExistingNonNegativeNumber(
            'numberOfVehicles',
            numberOfVehicles,
          );
        }

        if (crewMemberIds.find(id => !crewMembersMap[id])) {
          setError('crewMemberIds', t('validation.invalid-options-selected'));
        }

        if (!skipperId) {
          setError('skipperId', t('validation.required-field'));
        } else if (!crewMembersMap[skipperId]) {
          setError('skipperId', t('validation.invalid-option'));
        } else if (crewMemberIds.includes(skipperId)) {
          setError('skipperId', t('validation.duplicated-value'));
        }

        if (carriesPassengers) {
          Object.entries(numericPassengerValues).forEach(([key, value]) => {
            validateExistingNonNegativeNumber(
              key as keyof JourneyLogFormValues,
              value,
            );
          });
        }
      },
      differenceChecker: {
        crewMemberIds: (a: string[], b: string[]) =>
          a.length !== b.length ||
          (a.length > 0 && a.some(x => !b.includes(x))),
      },
      resetOnInitialValuesChange: () => Boolean(journeyLog || assignedVessels),
    },
    { shouldInputsBeDisabled },
  );

  const {
    formState: {
      errors,
      haveValuesChanged,
      hasVisibleErrors,
      requestEdition,
      cancelEdition,
      handleSubmit,
      isEditing,
    },
  } = formManager;

  const hasVesselFormAnyErrors = Boolean(
    errors.vesselId ||
      errors.fuel ||
      errors.cargoAmount ||
      errors.numberOfVehicles,
  );
  const hasPassengerFormAnyErrors = Boolean(
    errors.skipperId ||
      errors.crewMemberIds ||
      errors.adultIslanders ||
      errors.adultNonIslanders ||
      errors.under18Islanders ||
      errors.under18NonIslanders ||
      errors.under05Islanders ||
      errors.under05NonIslanders ||
      errors.studentIslanders ||
      errors.studentNonIslanders ||
      errors.over65Islanders ||
      errors.over65NonIslanders,
  );

  useEffect(() => {
    // If the journey entity exists, the form has to be in edit mode it there is no log already existing and it is needed
    if (!isPending && journeyId && !journeyLog && needsJourneyLog) {
      requestEdition();
    }
    // Only "isPending" needs to be tracked, as it will ensure it is executed only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  // If the journey log is not allowed, it redirects to the main details
  useEffect(() => {
    if (!isAllowed) {
      navigateToJourneyDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  return (
    <CommonMainPanelForm
      isEditing={isEditing}
      onSubmit={handleSubmit}
      onCancelEdition={cancelEdition}
      onEditionRequest={requestEdition}
      isSubmitDisabled={
        !haveValuesChanged || hasVisibleErrors || shouldInputsBeDisabled
      }
      isEditDisabled={shouldInputsBeDisabled}
      isSubmitting={isFetching}
    >
      <div className="grid grid-rows-[min-content_1fr]">
        <div>
          {generalValidationMessages.map(message => (
            <Alert key={message} className="mb-2" color="warning">
              {message}
            </Alert>
          ))}
        </div>
        <Tabs
          tabs={[
            {
              label: t('content.vessel'),
              icon: hasVesselFormAnyErrors
                ? 'error'
                : vesselValidationMessages.length > 0
                  ? 'warning'
                  : undefined,
            },
            {
              label: t('content.people'),
              icon: hasPassengerFormAnyErrors
                ? 'error'
                : passengersValidationMessages.length > 0
                  ? 'warning'
                  : undefined,
            },
          ]}
        >
          <VesselForm
            formManager={formManager}
            vessels={assignableVessels}
            vesselsMap={vesselsMap}
            isFetchingVessels={isFetchingVessels || isFetchingAssignedVessels}
            carriesCargo={carriesCargo}
            carriesVehicles={carriesVehicles}
            validationMessages={vesselValidationMessages}
          />
          <PassengersForm
            formManager={formManager}
            crewMembers={crewMembers}
            crewMembersMap={crewMembersMap}
            isFetchingCrewMembers={isFetchingCrewMembers}
            validationMessages={passengersValidationMessages}
            carriesPassengers={carriesPassengers}
            vesselsMap={vesselsMap}
          />
        </Tabs>
      </div>
    </CommonMainPanelForm>
  );
};

export default JourneyLogPage;
